import { type ReactNode } from "react";
import { CHIP_LABEL } from "./format";

// coloured tones (success/error/warning) emphasise a point; "secondary" is muted
export type AxisTone = "success" | "error" | "warning" | "secondary";

export type AxisColumn = {
  label: string;
  value: string; // pre-formatted ("—" when absent)
  tone?: AxisTone; // defaults to a muted "secondary" reference point
};

type AxisBarProps = {
  columns: AxisColumn[];
  footnote?: ReactNode;
};

const dotClass = (t: AxisTone): string =>
  t === "success"
    ? "bg-success-main"
    : t === "error"
      ? "bg-error-main"
      : t === "warning"
        ? "bg-warning-main"
        : "bg-mui-black-23 dark:bg-mui-white-38";

const valueClass = (t: AxisTone): string =>
  t === "secondary" ? "text-token-text-secondary" : "text-token-text-primary";

// Evenly-spaced labelled points on a connecting rule. Dots are decorative tone
// markers, not value-positioned, so the layout never re-scales as values stream
// in; centred columns put the outer dot centres 50/N % in so the rule meets them.
const AxisBar = ({ columns, footnote }: AxisBarProps) => {
  const inset = columns.length ? `${50 / columns.length}%` : "50%";
  return (
    <div>
      <div className="flex flex-col gap-1.5">
        <div className="flex">
          {columns.map(c => (
            <span key={c.label} className="flex-1 text-center text-xs uppercase tracking-wide text-token-text-secondary">
              {c.label}
            </span>
          ))}
        </div>
        <div className="relative flex">
          <div
            className="pointer-events-none absolute top-1/2 h-px -translate-y-1/2 bg-mui-black-23 dark:bg-mui-white-38"
            style={{ left: inset, right: inset }}
          />
          {columns.map(c => (
            <div key={c.label} className="flex flex-1 justify-center">
              <span className={`relative h-2.5 w-2.5 rounded-full ${dotClass(c.tone ?? "secondary")}`} />
            </div>
          ))}
        </div>
        <div className="flex">
          {columns.map(c => (
            <span key={c.label} className={`flex-1 text-center ${CHIP_LABEL} ${valueClass(c.tone ?? "secondary")}`}>
              {c.value}
            </span>
          ))}
        </div>
      </div>

      {footnote && <div className="mt-2 text-center text-xs text-token-text-secondary">{footnote}</div>}
    </div>
  );
};

export default AxisBar;
